/**
 * Client-side image compression for document uploads.
 * Compresses images to max 1920px and JPEG quality 0.82 — keeps documents
 * readable while reducing file size from 5-10 MB (phone photos) to ~300-700 KB.
 *
 * Non-image files (PDFs, etc.) pass through unchanged as base64 DataURL.
 *
 * Usage:
 *   const dataUrl = await compressIfImage(file);
 *   await api.post('/driver/upload-document/...', { document: dataUrl });
 */
export function compressIfImage(file, { maxDim = 1920, quality = 0.82 } = {}) {
  return new Promise((resolve) => {
    if (!file || !file.type || !file.type.startsWith('image/')) {
      const r = new FileReader();
      r.onloadend = () => resolve(r.result);
      r.readAsDataURL(file);
      return;
    }
    const img = new window.Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      let { width, height } = img;
      if (width > maxDim || height > maxDim) {
        const ratio = Math.min(maxDim / width, maxDim / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      const r = new FileReader();
      r.onloadend = () => resolve(r.result);
      r.readAsDataURL(file);
    };
    img.src = url;
  });
}
